import React from 'react'
import { EditorProps } from 'prosemirror-view'
import { ArticleFlat, Layer } from 'types'
import { LayerStatesProvider } from '../LayerStatesProvider/LayerStatesProvider'
import { StateWithPresenceProvider } from '../StateWithPresenceProvider/StateWithPresenceProvider'
import { useReactNodeViewPortals } from 'common/Prose/nodeViews/ReactNodeView/ReactnodeViewPortalsContext'
import { nerudaNodeViews } from './nerudaNodeViews'
import { ErrorBoundary } from '@sentry/react'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

interface Props {
  article: ArticleFlat
  layer: Layer
  children?: React.ReactNode
  editorProps?: EditorProps
  inCommentMode?: boolean
  readOnly?: boolean
}

const NerudaDocEditor = ({ article, children, inCommentMode, editorProps, readOnly }: Props) => {
  const { createPortal, removePortal } = useReactNodeViewPortals()

  const nodeViews: EditorProps['nodeViews'] = nerudaNodeViews({
    readOnly,
    inCommentMode,
    createPortal,
    removePortal,
  })

  return (
    <LayerStatesProvider article={article} inCommentMode={inCommentMode} readOnly={readOnly}>
      <StateWithPresenceProvider
        article={article}
        readOnly={readOnly}
        inCommentMode={inCommentMode}
        editorProps={{
          nodeViews,
          ...editorProps,
        }}
      >
        {children}
      </StateWithPresenceProvider>
    </LayerStatesProvider>
  )
}

const WithError = (props: Props) => (
  <ErrorBoundary
    onError={() =>
      showErrorMessageToast(
        'Failed to display document. Please try to refresh the page. If this error persists, please contact support.',
      )
    }
  >
    <NerudaDocEditor {...props} />
  </ErrorBoundary>
)

export { WithError as NerudaDocEditor }
